import { useEffect, useMemo, useState } from 'react';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { AutocompleteSelect } from '../../components/AutocompleteSelect';
import {
  listMyClientServicePairs,
  listMyClientRates,
  upsertClientRate,
  deleteClientRate,
  type ProClientServicePair,
  type ProClientRate,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

const clientLabel = (c: {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}) => {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  if (name && c.email) return `${name} · ${c.email}`;
  return name || c.email || 'Client';
};

const formatMoney = (amount: number | null): string => {
  if (amount == null || !Number.isFinite(amount)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
};

export default function ProClientRatesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Every (client, service) pair the pro has been booked for — the single
  // source for both autocompletes.
  const [pairs, setPairs] = useState<ProClientServicePair[]>([]);
  const [rates, setRates] = useState<ProClientRate[]>([]);

  // Add/set-a-rate form state.
  const [customerId, setCustomerId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [rateInput, setRateInput] = useState('');

  // Load the pro's (client, service) booking pairs + existing rates.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pairsRes, ratesRes] = await Promise.all([
          listMyClientServicePairs(),
          listMyClientRates(),
        ]);
        if (cancelled) return;
        setPairs(pairsRes.data ?? []);
        setRates(ratesRes.data ?? []);
      } catch (err) {
        if (!cancelled) {
          toast.error(apiErrorMessage(err, 'Could not load your client rates.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasAnyPairs = pairs.length > 0;
  const pairExists = (cid: string, sid: string) =>
    pairs.some((p) => p.customer_id === cid && p.service_id === sid);

  // Clients shown in the picker — when a service is chosen, only the
  // clients who've booked THAT service with this pro; otherwise everyone.
  const clientOptions = useMemo(() => {
    const relevant = serviceId
      ? pairs.filter((p) => p.service_id === serviceId)
      : pairs;
    const byId = new Map<string, ProClientServicePair>();
    for (const p of relevant) if (!byId.has(p.customer_id)) byId.set(p.customer_id, p);
    return [...byId.values()]
      .sort((a, b) => clientLabel(a).localeCompare(clientLabel(b)))
      .map((c) => ({
        value: c.customer_id,
        label:
          [c.first_name, c.last_name].filter(Boolean).join(' ').trim() ||
          c.email ||
          'Client',
        sublabel: c.email ?? undefined,
      }));
  }, [pairs, serviceId]);

  // Services shown in the picker — when a client is chosen, only the
  // services THEY'VE booked with this pro; otherwise everything booked.
  const serviceOptions = useMemo(() => {
    const relevant = customerId
      ? pairs.filter((p) => p.customer_id === customerId)
      : pairs;
    const byId = new Map<string, { id: string; name: string }>();
    for (const p of relevant)
      if (!byId.has(p.service_id))
        byId.set(p.service_id, { id: p.service_id, name: p.service_name });
    return [...byId.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((s) => ({ value: s.id, label: s.name }));
  }, [pairs, customerId]);

  // Picking one side cross-filters the other; clear the other selection if
  // the chosen pair no longer exists (e.g. that client never booked the
  // service that was already selected).
  const handleClientChange = (v: string) => {
    setCustomerId(v);
    if (v && serviceId && !pairExists(v, serviceId)) setServiceId('');
  };
  const handleServiceChange = (v: string) => {
    setServiceId(v);
    if (v && customerId && !pairExists(customerId, v)) setCustomerId('');
  };

  const reloadRates = async () => {
    try {
      const res = await listMyClientRates();
      setRates(res.data ?? []);
    } catch {
      // Non-fatal — the upsert/delete already succeeded; the list just
      // won't refresh. The page can be reloaded to recover.
    }
  };

  const handleSave = async () => {
    if (saving) return;
    const rate = Number(rateInput);
    if (!customerId) {
      toast.error('Pick a client first.');
      return;
    }
    if (!serviceId) {
      toast.error('Pick a service first.');
      return;
    }
    if (!Number.isFinite(rate) || rate <= 0) {
      toast.error('Enter a rate greater than zero.');
      return;
    }
    setSaving(true);
    try {
      const res = await upsertClientRate({
        customer_id: customerId,
        service_id: serviceId,
        custom_rate: rate,
      });
      toast.success(res.message || 'Client rate saved.');
      setRateInput('');
      await reloadRates();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the rate.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      const res = await deleteClientRate(id);
      toast.success(res.message || 'Client rate removed.');
      setRates((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not remove the rate.'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col font-['DM_Sans',sans-serif]">
      <HeaderPro />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-6 md:py-12">
        <div className="mb-8">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[24px] leading-[1.2] text-[#8e2a2f]">
            Client rates
          </h1>
          <p className="text-[14px] text-[#908e8e] mt-2 max-w-[640px]">
            Lock in an agreed rate for a repeat client on one of your services.
            It overrides your standard price for that client only — every other
            customer keeps your usual rate.
          </p>
        </div>

        {/* ---------------- Add / set a rate ---------------- */}
        {/* No overflow-hidden here: the Service/Client dropdown menus are
            absolutely positioned and would be clipped by it. The header
            rounds its own top corners to keep the card looking right. */}
        <div className="mb-12 border border-[#eee] rounded-[12px]">
          <div className="bg-[#f2eee2] px-6 py-3 rounded-t-[12px]">
            <p className="font-bold text-[14px] text-[#8e2a2f]">
              Add / set a rate
            </p>
          </div>
          <div className="px-6 py-5 flex flex-col md:flex-row md:items-end gap-4">
            <label className="flex flex-col gap-1 flex-1 min-w-0">
              <span className="text-[11px] text-[#908e8e] uppercase tracking-wide">
                Client
              </span>
              <AutocompleteSelect
                value={customerId}
                onChange={handleClientChange}
                options={clientOptions}
                placeholder="Select a client…"
                buttonClassName="h-[44px] w-full"
                emptyText={
                  serviceId
                    ? 'No clients have booked that service.'
                    : 'No clients yet.'
                }
              />
            </label>
            <label className="flex flex-col gap-1 flex-1 min-w-0">
              <span className="text-[11px] text-[#908e8e] uppercase tracking-wide">
                Service
              </span>
              <AutocompleteSelect
                value={serviceId}
                onChange={handleServiceChange}
                options={serviceOptions}
                placeholder="Select a service…"
                buttonClassName="h-[44px] w-full"
                emptyText={
                  customerId
                    ? "This client hasn't booked any of your services."
                    : 'No services booked yet.'
                }
              />
            </label>
            <label className="flex flex-col gap-1 shrink-0">
              <span className="text-[11px] text-[#908e8e] uppercase tracking-wide">
                Rate (£)
              </span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={rateInput}
                placeholder="0.00"
                onChange={(e) => setRateInput(e.target.value)}
                className="h-[44px] w-[140px] bg-white border border-[#eee] rounded-lg px-3 text-[14px] font-bold text-[#3a3335] outline-none focus:border-[#8e2a2f]"
              />
            </label>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              className="h-[44px] px-6 rounded-[8px] bg-[#8e2a2f] text-white text-[14px] font-bold hover:bg-[#7a2228] transition-colors disabled:opacity-60 shrink-0"
            >
              {saving ? 'Saving…' : 'Save rate'}
            </button>
          </div>
          {!loading && !hasAnyPairs ? (
            <p className="px-6 pb-5 text-[13px] text-[#908e8e]">
              You don't have any past clients yet. Once you've completed a
              booking for a customer, they and the service they booked will
              show up here.
            </p>
          ) : null}
        </div>

        {/* ---------------- Existing rates ---------------- */}
        <div className="border border-[#eee] rounded-[12px] overflow-hidden">
          <div className="bg-[#f2eee2] px-6 py-3">
            <p className="font-bold text-[14px] text-[#8e2a2f]">Your rates</p>
          </div>

          {loading ? (
            <p className="px-6 py-6 text-sm text-[#908e8e]">Loading…</p>
          ) : rates.length === 0 ? (
            <p className="px-6 py-6 text-sm text-[#908e8e]">
              No client rates set yet. Use the form above to agree a rate with a
              repeat client.
            </p>
          ) : (
            <div className="divide-y divide-[#eee]">
              {rates.map((rate) => (
                <div
                  key={rate.id}
                  className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-6 py-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[15px] tracking-[-0.1px] text-[#3a3335] truncate">
                      {clientLabel(rate)}
                    </p>
                    <p className="text-[13px] text-[#908e8e]">
                      {rate.service_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <p className="font-bold text-[15px] text-[#3a3335] whitespace-nowrap">
                      {formatMoney(rate.custom_rate)}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleDelete(rate.id)}
                      disabled={deletingId === rate.id}
                      className="h-[36px] px-4 rounded-[8px] text-[13px] font-bold text-[#8e2a2f] bg-[#8e2a2f]/[0.06] hover:bg-[#8e2a2f]/[0.12] transition-colors disabled:opacity-60"
                    >
                      {deletingId === rate.id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
